import { memo, useCallback, useMemo, useState } from 'react'

import { fetchPartnerLnRStatus } from 'clickNgo/entities/partner/api'
import { IPartner, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { PartnerSpecialization } from 'clickNgo/entities/partner/models/specialization'
import { CompanyDetailsFlags } from 'clickNgo/entities/partner/ui/companyDetailsFlags'
import { CompanySalesTeam } from 'clickNgo/entities/partner/ui/companySalesTeam'
import { PartnerSpecializationCarousel } from 'clickNgo/entities/partner/ui/partnerSpecializationCarousel'
import { IAttachment } from 'clickNgo/models/file'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'

import {
  CompanyDetailsActions,
  CompanyDetailsHeader,
  CompanyDetailsItem,
  PartnerStatusModal,
} from './components'
import { CompanyDetailsStatus } from './components/companyDetailsStatus'
import { getCompanyAddress, getCompanyContractType } from './helpers'
import { HandlerProps } from './models'
import { companyDetailsTranslations } from './translation'

import styles from './CompanyDetails.module.scss'

type Props = {
  partner: IPartner
  partnerFlags: IPartnerFlags
  partnerAttachments: IAttachment[]
  partnerSpecializations: PartnerSpecialization[]
} & HandlerProps

export const CompanyDetails = memo<Props>((props) => {
  const [isPartnerStatusModalOpen, setIsPartnerStatusModalOpen] = useState(false)
  const agreements = useMemo(
    () => props.partnerFlags.agreement.map(({ code }) => code) || [],
    [props.partnerFlags.agreement]
  )
  const companyAddress = getCompanyAddress(props.partner)
  const companyContractType = getCompanyContractType(props.partner.partnerType)
  const hpMemberSince =
    props.partner.hpMemberSince && getFormatDate(props.partner.hpMemberSince, 'date')
  const hpPartnerTypeStartDate =
    props.partner.partnerTypeStartDate && getFormatDate(props.partner.partnerTypeStartDate, 'date')
  const lnrStatus = useMemo(async () => {
    const reportDetails = await fetchPartnerLnRStatus(props.partner.partnerId)
    return reportDetails?.approvalStatus || '-'
  }, [props.partner.partnerId])
  const companyNameTip = useCallback(
    () => <>{props.partner.communicationName}</>,
    [props.partner.communicationName]
  )
  const companyAddressTip = useCallback(() => <>{companyAddress}</>, [companyAddress])
  const openPartnerStatusModal = useCallback(() => setIsPartnerStatusModalOpen(true), [])
  const closePartnerStatusModal = useCallback(() => setIsPartnerStatusModalOpen(false), [])

  return (
    <div className={styles.container}>
      <CompanyDetailsHeader partner={props.partner} partnerFlags={props.partnerFlags} />
      <div className={styles.content}>
        <div className={styles.block}>
          <Tooltip tipContent={companyNameTip}>
            <div className={styles.companyName}>{props.partner.communicationName}</div>
          </Tooltip>
          <Tooltip tipContent={companyAddressTip}>
            <div className={styles.companyAddress}>{companyAddress}</div>
          </Tooltip>
        </div>
        <div className={styles.block}>
          <CompanyDetailsFlags
            partnerId={props.partner.partnerId}
            isAmplifyMember={props.partner.isAmplifyMember}
            agreements={agreements}
            specialPricingFlag={props.partner.splPriceFlag}
          />
        </div>
        <hr />
        <div className={styles.block}>
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.hpInAgrimentWithHP}
            value={hpMemberSince}
          />
          <RenderWhen condition={Boolean(companyContractType)}>
            <CompanyDetailsItem label={companyContractType} value={hpPartnerTypeStartDate} />
          </RenderWhen>
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.lnrStatus}
            value={lnrStatus}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.pricingType}
            value={props.partner.resellerAbInd}
          />
          <div className={styles.partnerStatusButton}>
            <Button onClick={openPartnerStatusModal} color="primary" variant="contained">
              <Translation text={companyDetailsTranslations.actions.partnerStatusButton} />
            </Button>
          </div>
        </div>
        <div className={styles.block}>
          <CompanyDetailsStatus partner={props.partner} partnerFlags={props.partnerFlags} />
        </div>
        <PartnerStatusModal
          partnerId={props.partner.partnerId}
          isOpen={isPartnerStatusModalOpen}
          onClose={closePartnerStatusModal}
        />
        <div className={styles.block}>
          <PartnerSpecializationCarousel partnerSpecializations={props.partnerSpecializations} />
        </div>
        <div className={styles.block}>
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.market}
            value={props.partner.market}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.organization}
            value={props.partner.organization}
          />
        </div>
        <hr />
        <div className={styles.block}>
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.taxId}
            value={props.partner.taxId}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.parentPartnerId}
            value={props.partner.parentPartnerId}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.partnerId}
            value={props.partner.partnerId}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.locationId}
            value={props.partner.locationId}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.reporterId}
            value={props.partner.reporterId}
          />
          <CompanyDetailsItem
            label={companyDetailsTranslations.details.storeNumber}
            value={props.partner.storeNumber}
          />
        </div>
        <div className={styles.block}>
          <CompanyDetailsActions {...props} />
        </div>
        <div className={styles.salesTeamContainer}>
          <CompanySalesTeam partner={props.partner} />
        </div>
      </div>
    </div>
  )
})

CompanyDetails.displayName = 'CompanyDetails'
